import React, {
  useCallback,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  FormEvent,
} from "react";

import { Button, FormField, FormInput } from "@bared/ui";

import { Sheet } from "~/components/Sheet";

import { useFunctionsGatsby } from "~/hooks/useFunctionsGatsby";
import { useCustomer } from "~/hooks/useSession";

export interface BackInStockSheetProps {
  /** Determines if the sheet should be open or closed */
  active: boolean;
  /** Sets the active state of the sheet */
  setActive: Dispatch<SetStateAction<boolean>>;
  /** The ID of the selected variant */
  selectedVariantId: string | null;
}

export function BackInStockSheet(props: BackInStockSheetProps) {
  const { backInStock, loading, errors, setErrors } = useFunctionsGatsby();
  const customer = useCustomer();

  const [email, setEmail] = useState<string | undefined>(undefined);
  const [sent, setSent] = useState<boolean>(false);

  const variantId = props.selectedVariantId
    ? props.selectedVariantId.split(`ProductVariant/`)[1]
    : null;

  const handleSheetToggle = useCallback(() => {
    props.setActive((prev) => !prev);
    setEmail(undefined);
    setSent(false);
    setErrors([]);
  }, [props.active]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!email || !variantId) return;

    const { status, body } = await backInStock(email, variantId);

    if (status === "success") {
      setSent(true);
    } else {
      setErrors([body as string]);
      setSent(false);
    }
  };

  useEffect(() => {
    if (customer?.email && !email) {
      setEmail(customer.email);
    }
  }, [customer]);

  return (
    <Sheet
      isOpen={props.active}
      onClose={handleSheetToggle}
      layout="condensed"
      className="p-midi md:px-maxi md:py-midi"
      eventTitle="Back in Stock"
    >
      {!sent ? (
        <form className="rte flex flex-col" onSubmit={handleSubmit}>
          <h3>Notify Me</h3>
          <p className="text-hint leading-hint">
            Pop your email below and we'll let you know as soon as this item is
            back in stock.
          </p>

          <div className="mt-mini flex flex-col space-y-mini">
            <FormField name="email" label="Email" required errors={errors}>
              <FormInput
                type="email"
                autoComplete="email"
                onChange={({ target: { value } }) => setEmail(value)}
                value={email}
                isError={errors && errors.length > 0}
                required
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              size="large"
              className="w-full"
              disabled={loading}
            >
              Send
            </Button>
          </div>
        </form>
      ) : (
        <div className="rte">
          <h3>Done!</h3>
          <p className="text-hint leading-hint">
            You'll receive an email once this item is back in stock.
          </p>
          <Button
            className="mt-mini w-full"
            variant="secondary"
            size="large"
            onClick={handleSheetToggle}
          >
            Continue Shopping
          </Button>
        </div>
      )}
    </Sheet>
  );
}
